import React, { useState, useEffect } from 'react';
import { Modal, Box, IconButton, Button, Radio, CircularProgress } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import contactsService from '../../axios/contactsService';
import vendorsService from '../../axios/vendorsService';
import { UPLOAD_TYPES } from '../../utils/uploadTypes';
import { validateHeaders, validateRows } from '../../utils/csvContactValidation';
import { ContactCsvUpload } from './ContactCsvUpload';
import { ContactPreviewTable } from './ContactPreviewTable';
import { ContactUploadStatus } from './ContactUploadStatus';
import styles from './ContactUploadModal.module.css';

const initialFileState = {
  step: 'idle',
  file: null,
  validRows: [],
  clientErrors: [],
  uploadId: null,
  submitError: null,
  finalStatus: null,
  submitting: false,
};

export const ContactUploadModal = ({ open, onClose, isVendor, isBroker, vendorGroupNumber }) => {
  const callerRole = isBroker ? 'BROKER' : 'SUPPLIER';
  const availableTypeKeys = Object.keys(UPLOAD_TYPES).filter((key) => UPLOAD_TYPES[key].callerRole === callerRole);

  const [uploadType, setUploadType] = useState(availableTypeKeys[0]);
  const [state, setState] = useState(initialFileState);
  const [childVendorNumbers, setChildVendorNumbers] = useState(null);
  const [childVendorNumbersError, setChildVendorNumbersError] = useState(false);

  useEffect(() => {
    if (!availableTypeKeys.includes(uploadType)) {
      setUploadType(availableTypeKeys[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerRole]);

  useEffect(() => {
    if (!open || callerRole !== 'SUPPLIER' || !vendorGroupNumber) {
      return;
    }
    let cancelled = false;
    setChildVendorNumbers(null);
    setChildVendorNumbersError(false);
    vendorsService.getChildVendors(vendorGroupNumber)
      .then((result) => {
        if (cancelled) return;
        setChildVendorNumbers((result?.data || []).map((v) => v.vendorNumber));
      })
      .catch(() => {
        if (cancelled) return;
        setChildVendorNumbersError(true);
      });
    return () => { cancelled = true; };
  }, [open, callerRole, vendorGroupNumber]);

  const type = UPLOAD_TYPES[uploadType] || {};

  const reset = () => setState(initialFileState);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleTypeChange = (e) => {
    setUploadType(e.target.value);
    reset();
  };

  const handleFileParsed = (file, rows, headerFields) => {
    const { missing } = validateHeaders(uploadType, headerFields);

    if (missing.length > 0) {
      setState({
        ...initialFileState,
        step: 'file',
        file,
        clientErrors: [{ rowNumber: null, contactEmail: null, reason: `Missing required column(s): ${missing.join(', ')}` }],
        validRows: [],
      });
      return;
    }

    const { validRows, errors } = validateRows(uploadType, rows, {
      validVendorNumbers: callerRole === 'SUPPLIER' ? childVendorNumbers : null,
    });
    setState({ ...initialFileState, step: 'file', file, validRows, clientErrors: errors });
  };

  const handleUpload = async () => {
    setState((prev) => ({ ...prev, submitError: null, submitting: true }));

    try {
      const { uploadId, uploadUrl } = await contactsService.getUploadUrl(state.file.name, uploadType);

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        body: state.file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload to storage failed with status ${putResponse.status}`);
      }

      await contactsService.registerUpload(uploadId, state.validRows.length);

      setState((prev) => ({ ...prev, step: 'uploading', uploadId }));
    } catch (err) {
      setState((prev) => ({ ...prev, submitError: 'Something went wrong starting the upload. Please try again.', submitting: false }));
    }
  };

  const duplicateCount = state.clientErrors.filter((e) => e.reason && e.reason.startsWith('Duplicate of')).length;
  const missingVendorNumber = callerRole === 'SUPPLIER' && !vendorGroupNumber;
  const loadingChildVendors = callerRole === 'SUPPLIER' && !missingVendorNumber && childVendorNumbers === null && !childVendorNumbersError;
  const uploadBlocked = missingVendorNumber || loadingChildVendors;

  return (
    <Modal className="embed-popup" open={open} onClose={handleClose}>
      <Box className={styles['modal-box']}>
        <div className={styles['modal-head']}>
          <h3>Bulk Upload Contacts</h3>
          <IconButton onClick={handleClose} aria-label="Close">
            <ClearIcon color="error" fontSize="small" />
          </IconButton>
        </div>

        <div className={styles['modal-body']}>
          {missingVendorNumber && (
            <div className={`${styles['result-banner']} ${styles.warn}`} style={{ marginBottom: 14 }}>
              <p>Your account isn&apos;t linked to a vendor yet. Contact your Shared Table administrator before uploading contacts.</p>
            </div>
          )}

          {!missingVendorNumber && loadingChildVendors && (
            <div className={styles['summary-line']}>Loading your vendor information…</div>
          )}

          {!missingVendorNumber && childVendorNumbersError && (
            <div className={`${styles['result-banner']} ${styles.warn}`} style={{ marginBottom: 14 }}>
              <p>We couldn&apos;t load your vendor information. Please close and reopen this dialog to try again.</p>
            </div>
          )}

          {!uploadBlocked && availableTypeKeys.length > 1 && (
            <div className={styles['type-select']}>
              {availableTypeKeys.map((key) => (
                <label key={key} className={styles['type-option']}>
                  <Radio
                    size="small"
                    checked={uploadType === key}
                    value={key}
                    onChange={handleTypeChange}
                    disabled={state.step !== 'idle'}
                  />
                  {UPLOAD_TYPES[key].label}
                </label>
              ))}
            </div>
          )}

          {!uploadBlocked && state.step === 'idle' && (
            <>
              <ContactCsvUpload onFileParsed={handleFileParsed} />
              <div className={styles['helper-row']}>
                <span>Required columns: {(type.columnLabels || []).join(', ')}</span>
                <Button className={styles['link-btn']} href={type.templateUrl} download>
                  Download Template
                </Button>
              </div>
            </>
          )}

          {!uploadBlocked && state.step === 'file' && (
            <>
              <div className={styles['file-chip']}>
                <InsertDriveFileOutlinedIcon fontSize="small" />
                <div className={styles['file-meta']}>
                  <strong>{state.file.name}</strong>
                  <span>{Math.max(1, Math.round(state.file.size / 1024))} KB</span>
                </div>
              </div>
              <div className={styles['summary-line']}>
                <span>{state.validRows.length} row{state.validRows.length === 1 ? '' : 's'} ready to upload</span>
                {duplicateCount > 0 && (
                  <>
                    <span className={styles.dot} />
                    <span>{duplicateCount} duplicate row{duplicateCount === 1 ? '' : 's'}</span>
                  </>
                )}
              </div>
              <ContactPreviewTable errors={state.clientErrors} />
              {state.submitError && (
                <div className={`${styles['result-banner']} ${styles.warn}`} style={{ marginTop: 12 }}>
                  <p>{state.submitError}</p>
                </div>
              )}
              <div className={styles['btn-row']}>
                <Button className={styles['cancel-btn']} onClick={reset} disabled={state.submitting}>Cancel</Button>
                <Button
                  className={styles['submit-btn']}
                  disabled={state.validRows.length === 0 || state.submitting}
                  onClick={handleUpload}
                  startIcon={state.submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {state.submitting ? 'Uploading…' : 'Upload & Validate'}
                </Button>
              </div>
            </>
          )}

          {!uploadBlocked && state.step === 'uploading' && (
            <>
              <ContactUploadStatus
                uploadId={state.uploadId}
                onStatusChange={(finalStatus) => setState((prev) => ({ ...prev, finalStatus }))}
              />
              {state.finalStatus && (
                <div className={styles['btn-row']}>
                  <Button className={styles['cancel-btn']} onClick={reset}>Upload another file</Button>
                  <Button className={styles['submit-btn']} onClick={handleClose}>Done</Button>
                </div>
              )}
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
};
